// Section 3 new-game options: presets (Gentle / Standard / Unforgiving, Section 26) above the
// detailed form; every option; a blank seed becomes a random one, shown after the start.
import { useState } from 'preact/hooks';
import type { NewGameOptions } from '../../sim/api';
import type { NewGameFormVM } from '../../viewModels';
import { uiText } from '../text/en';

interface Props {
  vm: NewGameFormVM;
  onStart: (options: NewGameOptions, seed: string) => void;
}

type ChoiceKey = 'land' | 'disasterFrequency' | 'threatLevel' | 'startingStock' | 'startingKnowledge' | 'steadyTargetYears';

const CHOICES: { key: ChoiceKey; id: string; label: string; group: string; values: readonly NewGameOptions[ChoiceKey][] }[] = [
  { key: 'land', id: 'land', label: uiText.title.land, group: 'land', values: ['fertile', 'temperate', 'harsh'] },
  { key: 'disasterFrequency', id: 'disasters', label: uiText.title.disasters, group: 'disasters', values: ['calm', 'normal', 'volatile'] },
  { key: 'threatLevel', id: 'threat', label: uiText.title.threat, group: 'threat', values: ['none', 'low', 'high'] },
  { key: 'startingStock', id: 'stock', label: uiText.title.stock, group: 'stock', values: ['lean', 'standard', 'generous'] },
  { key: 'startingKnowledge', id: 'knowledge', label: uiText.title.knowledge, group: 'knowledge', values: ['novice', 'mixed', 'skilled'] },
  { key: 'steadyTargetYears', id: 'target', label: uiText.title.target, group: 'target', values: [5, 10, 20] },
];

function randomSeed(): string {
  // UI-only randomness is allowed outside src/sim (24.13); the seed is then fixed for the run.
  return Math.random().toString(36).slice(2, 10);
}

export function NewGameForm({ vm, onStart }: Props) {
  const [options, setOptions] = useState<NewGameOptions>(vm.defaults);
  const t = uiText.title;
  const set = <K extends keyof NewGameOptions>(key: K, value: NewGameOptions[K]): void => {
    setOptions({ ...options, [key]: value, preset: null });
  };
  return (
    <form
      class="title__form"
      aria-labelledby="new-game-title"
      onSubmit={(e) => {
        e.preventDefault();
        const seed = options.seed.trim() === '' ? randomSeed() : options.seed.trim();
        onStart({ ...options, seed }, seed);
      }}
    >
      <h2 id="new-game-title">{t.newGame}</h2>
      <fieldset class="presets">
        <legend>{t.presets}</legend>
        {vm.presets.map((p) => (
          <button
            key={p.id}
            type="button"
            class={`button preset${options.preset === p.id ? ' is-selected' : ''}`}
            aria-pressed={options.preset === p.id}
            onClick={() => setOptions({ ...p.options, seed: options.seed, population: options.population })}
          >
            <span class="preset__name">{t.preset[p.id] ?? p.id}</span>
            <span class="preset__note">{t.presetNote[p.id] ?? ''}</span>
          </button>
        ))}
      </fieldset>
      <fieldset class="options">
        <legend>{t.options}</legend>
        <div class="field">
          <label class="field__label" for="population">
            {t.population} <span class="num">{options.population}</span>
          </label>
          <input
            id="population"
            type="range"
            min={vm.populationMin}
            max={vm.populationMax}
            value={options.population}
            onInput={(e) => set('population', Number(e.currentTarget.value))}
          />
          <span class="presets presets--inline">
            {vm.populationPresets.map((n) => (
              <button
                key={n}
                type="button"
                class={`button button--small${options.population === n ? ' is-selected' : ''}`}
                aria-pressed={options.population === n}
                onClick={() => set('population', n)}
              >
                {n}
              </button>
            ))}
          </span>
        </div>
        <label class="field" for="seed">
          <span class="field__label">{t.seed}</span>
          <input id="seed" type="text" value={options.seed} onInput={(e) => set('seed', e.currentTarget.value)} />
        </label>
        {CHOICES.map((c) => (
          <label key={c.id} class="field" for={c.id}>
            <span class="field__label">{c.label}</span>
            <select
              id={c.id}
              value={String(options[c.key])}
              onChange={(e) => {
                const found = c.values.find((v) => String(v) === e.currentTarget.value);
                if (found !== undefined) set(c.key, found as never);
              }}
            >
              {c.values.map((v) => (
                <option key={String(v)} value={String(v)}>
                  {t.choices[c.group]?.[String(v)] ?? String(v)}
                </option>
              ))}
            </select>
          </label>
        ))}
        <div class="field">
          <span class="field__label">{t.cadence}</span>
          <span>{t.cadenceValue}</span>
        </div>
        <label class="field field--check" for="ironman">
          <input
            id="ironman"
            type="checkbox"
            checked={options.ironman}
            onChange={(e) => set('ironman', e.currentTarget.checked)}
          />
          <span>{t.ironman}</span>
        </label>
        <label class="field field--check" for="observer">
          <input
            id="observer"
            type="checkbox"
            checked={options.observer}
            onChange={(e) => set('observer', e.currentTarget.checked)}
          />
          <span>{t.observer}</span>
        </label>
      </fieldset>
      <button type="submit" class="button button--primary">
        {t.start}
      </button>
    </form>
  );
}
